import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const VB = '/api/vibevoice'

// ---------------------------------------------------------------------------
// Reading scripts
// ---------------------------------------------------------------------------

const SCRIPTS = [
  {
    id: 'standard',
    label: 'Standard (~15s)',
    text: 'The bright morning sun cast long shadows across the quiet village square. Fresh bread from the bakery filled the air with warmth, and children\'s laughter echoed through the narrow stone streets. By noon, the market was alive with vendors calling out their wares, each voice distinct and clear.',
    tip: 'Speak at your natural pace. Do not rush. The first take is usually the best.',
  },
  {
    id: 'short',
    label: 'Quick (~8s)',
    text: 'Please call Stella. Ask her to bring these things with her from the store: six spoons of fresh snow peas, five thick slabs of blue cheese, and maybe a snack for her brother Bob.',
    tip: 'Classic phoneme-rich sentence set. Good for a fast calibration.',
  },
  {
    id: 'extended',
    label: 'Extended (~30s)',
    text: 'When the sunlight strikes raindrops in the air, they act as a prism and form a rainbow. The rainbow is a division of white light into many beautiful colors. These take the shape of a long round arch, with its path high above, and its two ends apparently beyond the horizon. There is, according to legend, a boiling pot of gold at one end. People look, but no one ever finds it.',
    tip: 'Adapted from the Rainbow Passage. Read naturally — do not perform.',
  },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VbProfile {
  id: string
  name: string
  description: string | null
  language: string
  voice_type: string
  sample_count: number
  generation_count: number
  created_at: string
}

interface VbModelStatus {
  model_name: string
  display_name: string
  downloaded: boolean
  downloading: boolean
  loaded: boolean
  size_mb: number | null
}

interface VbGeneration {
  id: string
  profile_id: string
  text: string
  status: string
  duration: number | null
  error: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ModelBadge({ status }: { status: VbModelStatus | undefined }) {
  if (!status) return <span className="text-xs text-gray-400">checking…</span>
  if (status.loaded) return <span className="text-xs text-green-500 font-medium">loaded</span>
  if (status.downloaded) return <span className="text-xs text-blue-500 font-medium">ready (not loaded)</span>
  if (status.downloading) return <span className="text-xs text-amber-500 font-medium animate-pulse">downloading…</span>
  return <span className="text-xs text-red-400 font-medium">not downloaded</span>
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function VoiceCloningPage() {
  const queryClient = useQueryClient()
  const [scriptIdx, setScriptIdx] = useState(0)
  const script = SCRIPTS[scriptIdx] ?? SCRIPTS[0]!

  // Recording
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)

  // Upload file
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null)

  // Profile creation
  const [profileName, setProfileName] = useState('')
  const [referenceText, setReferenceText] = useState('')
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null)

  // Test generation
  const [testText, setTestText] = useState('The quick brown fox jumps over the lazy dog.')
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Status
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveMsg, setSaveMsg] = useState('')

  // ---------------------------------------------------------------------------
  // Data queries
  // ---------------------------------------------------------------------------

  const { data: profiles = [] } = useQuery<VbProfile[]>({
    queryKey: ['vb-profiles'],
    queryFn: () => fetch(`${VB}/profiles`).then(r => r.json()),
    refetchInterval: 8000,
  })

  const { data: modelStatuses } = useQuery<{ models: VbModelStatus[] }>({
    queryKey: ['vb-models'],
    queryFn: () => fetch(`${VB}/models/status`).then(r => r.json()),
    refetchInterval: 3000,
  })

  const cloneModel = modelStatuses?.models?.find(m => m.model_name === 'qwen-custom-voice-0.6B')
    ?? modelStatuses?.models?.find(m => m.model_name === 'qwen-custom-voice-1.7B')

  const { data: generation } = useQuery<VbGeneration>({
    queryKey: ['vb-generation', generationId],
    queryFn: () => fetch(`${VB}/history/${generationId}`).then(r => r.json()),
    enabled: !!generationId,
    refetchInterval: generationId && generating ? 1500 : false,
  })

  useEffect(() => {
    if (generation?.status === 'completed' || generation?.status === 'failed') {
      setGenerating(false)
    }
  }, [generation?.status])

  // ---------------------------------------------------------------------------
  // Waveform
  // ---------------------------------------------------------------------------

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current
    const analyser = analyserRef.current
    if (!canvas || !analyser) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const bufLen = analyser.frequencyBinCount
    const data = new Uint8Array(bufLen)
    analyser.getByteTimeDomainData(data)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = 'rgba(15,15,25,0.95)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.lineWidth = 2
    ctx.strokeStyle = '#a78bfa'
    ctx.beginPath()
    const slice = canvas.width / bufLen
    let x = 0
    for (let i = 0; i < bufLen; i++) {
      const v = (data[i] ?? 128) / 128.0
      const y = (v * canvas.height) / 2
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      x += slice
    }
    ctx.lineTo(canvas.width, canvas.height / 2)
    ctx.stroke()
    animRef.current = requestAnimationFrame(drawWaveform)
  }, [])

  // ---------------------------------------------------------------------------
  // Record
  // ---------------------------------------------------------------------------

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const actx = new AudioContext()
      const source = actx.createMediaStreamSource(stream)
      const analyser = actx.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)
      analyserRef.current = analyser
      animRef.current = requestAnimationFrame(drawWaveform)
      chunksRef.current = []
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        actx.close()
        if (animRef.current) cancelAnimationFrame(animRef.current)
        analyserRef.current = null
        const recorded = new Blob(chunksRef.current, { type: 'audio/webm' })
        setBlob(recorded)
        if (blobUrl) URL.revokeObjectURL(blobUrl)
        setBlobUrl(URL.createObjectURL(recorded))
        setUploadFile(null)
        setUploadPreviewUrl(null)
      }
      mr.start(100)
      mediaRef.current = mr
      setRecording(true)
      setElapsed(0)
      setBlob(null)
      setBlobUrl(null)
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)
    } catch (err: any) {
      alert(`Microphone access denied: ${err.message}`)
    }
  }

  const stopRecording = () => {
    mediaRef.current?.stop()
    mediaRef.current = null
    setRecording(false)
    if (timerRef.current) clearInterval(timerRef.current)
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setUploadFile(f)
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
    setUploadPreviewUrl(URL.createObjectURL(f))
    setBlob(null)
    setBlobUrl(null)
  }

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (animRef.current) cancelAnimationFrame(animRef.current)
    if (blobUrl) URL.revokeObjectURL(blobUrl)
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
    if (pollingRef.current) clearInterval(pollingRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Create profile + upload sample
  // ---------------------------------------------------------------------------

  const handleCreateProfile = async () => {
    const audioBlob = blob ?? uploadFile
    if (!audioBlob) return
    if (!profileName.trim()) { setSaveMsg('Enter a profile name.'); setSaveStatus('error'); return }
    if (!referenceText.trim()) { setSaveMsg('Enter the text you read aloud (reference transcript).'); setSaveStatus('error'); return }

    setSaveStatus('saving')
    setSaveMsg('')

    try {
      // 1. Create profile
      const profileRes = await fetch(`${VB}/profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: profileName.trim(), voice_type: 'cloned', language: 'en' }),
      })
      if (!profileRes.ok) {
        const errBody = await profileRes.json().catch(() => ({}))
        throw new Error(`Profile create failed: ${profileRes.status} — ${JSON.stringify(errBody)}`)
      }
      const profile: VbProfile = await profileRes.json()

      // 2. Upload sample
      const fd = new FormData()
      const ext = blob ? '.webm' : (uploadFile as File).name.split('.').pop() || 'wav'
      fd.append('file', audioBlob, `sample.${ext}`)
      fd.append('reference_text', referenceText.trim())
      const sampleRes = await fetch(`${VB}/profiles/${profile.id}/samples`, { method: 'POST', body: fd })
      if (!sampleRes.ok) {
        const errBody = await sampleRes.json().catch(() => ({}))
        throw new Error(`Sample upload failed: ${sampleRes.status} — ${JSON.stringify(errBody)}`)
      }

      setSaveStatus('saved')
      setSaveMsg(`Profile "${profile.name}" created with sample uploaded.`)
      setActiveProfileId(profile.id)
      setProfileName('')
      setReferenceText('')
      setBlob(null); setBlobUrl(null)
      setUploadFile(null); setUploadPreviewUrl(null)
      queryClient.invalidateQueries({ queryKey: ['vb-profiles'] })
    } catch (err: any) {
      setSaveStatus('error')
      setSaveMsg(err.message)
    }
  }

  // ---------------------------------------------------------------------------
  // Generate
  // ---------------------------------------------------------------------------

  const handleGenerate = async () => {
    if (!activeProfileId) return
    if (!testText.trim()) return
    if (!cloneModel?.downloaded) { setGenError('Model not downloaded yet. Wait for download to complete.'); return }

    setGenerating(true)
    setGenError(null)
    setGenerationId(null)

    try {
      const res = await fetch(`${VB}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: activeProfileId,
          text: testText.trim(),
          engine: 'qwen_custom_voice',
          language: 'en',
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Generation failed: ${res.status}`)
      }
      const gen: VbGeneration = await res.json()
      setGenerationId(gen.id)
      if (gen.status === 'completed' || gen.status === 'failed') setGenerating(false)
    } catch (err: any) {
      setGenerating(false)
      setGenError(err.message)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fetch(`${VB}/profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vb-profiles'] })
      if (activeProfileId && profiles.find(p => p.id === activeProfileId)) setActiveProfileId(null)
    },
  })

  const hasAudio = !!(blob || uploadFile)
  const previewUrl = blobUrl ?? uploadPreviewUrl
  const audioUrl = generation?.status === 'completed' ? `${VB}/audio/${generation.id}` : null

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">

      {/* Header + model status */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Voice Cloning Studio</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Powered by Voicebox + Qwen-TTS. Record or upload a 10–30s reference clip, create a profile, then generate speech in your voice.
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Clone model</p>
          <ModelBadge status={cloneModel} />
          {cloneModel?.downloading && (
            <p className="text-xs text-amber-500 mt-0.5">Download in progress…</p>
          )}
          {!cloneModel?.downloaded && !cloneModel?.downloading && (
            <button
              onClick={() => fetch(`${VB}/models/download`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ model_name: 'qwen-custom-voice-1.7B' }) })}
              className="text-xs text-violet-500 hover:underline mt-0.5 block"
            >
              Start download
            </button>
          )}
        </div>
      </div>

      {/* Script selection */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-900 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-gray-900 dark:text-white">Reading Script</h2>
          <div className="flex gap-2">
            {SCRIPTS.map((s, i) => (
              <button key={s.id} onClick={() => { setScriptIdx(i); setReferenceText(s.text) }}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${scriptIdx === i
                  ? 'bg-violet-600 border-violet-600 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-violet-400'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <blockquote className="rounded-xl bg-gray-50 dark:bg-surface-800 border-l-4 border-violet-500 px-4 py-3 text-base leading-relaxed text-gray-800 dark:text-gray-200 font-serif italic">
          {script.text}
        </blockquote>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-violet-500">Tip:</span> {script.tip}
        </p>
      </section>

      {/* Recorder */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-900 p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">Record</h2>
        <canvas ref={canvasRef} width={560} height={80}
          className={`w-full rounded-xl transition-opacity ${recording ? 'opacity-100' : 'opacity-30'}`}
          style={{ background: 'rgba(15,15,25,0.95)' }} />
        <div className="flex items-center gap-4">
          {!recording ? (
            <button onClick={startRecording}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-medium transition-colors">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />
              Start Recording
            </button>
          ) : (
            <button onClick={stopRecording}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-colors">
              <span className="w-2.5 h-2.5 rounded-sm bg-white inline-block" />
              Stop
            </button>
          )}
          {recording && (
            <span className="text-sm font-mono text-red-400 animate-pulse">
              {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
            </span>
          )}
        </div>
        {previewUrl && !recording && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Preview</p>
            <audio controls src={previewUrl} className="w-full rounded-lg" />
          </div>
        )}
      </section>

      {/* Upload */}
      <section className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-900 p-5 space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-white">Or upload an existing clip</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">WAV, MP3, OGG, or WebM. 10–30 seconds, clean speech, single speaker.</p>
        <label className="block">
          <span className="sr-only">Choose audio file</span>
          <input type="file" accept="audio/*" onChange={onFileChange}
            className="block text-sm text-gray-500 dark:text-gray-400
              file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0
              file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700
              dark:file:bg-violet-900/30 dark:file:text-violet-300
              hover:file:bg-violet-100 dark:hover:file:bg-violet-900/50 cursor-pointer" />
        </label>
        {uploadPreviewUrl && !blob && <audio controls src={uploadPreviewUrl} className="w-full rounded-lg" />}
      </section>

      {/* Create profile */}
      {hasAudio && (
        <section className="rounded-2xl border border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/30 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Create Voice Profile</h2>
          <div className="space-y-3">
            <input type="text" placeholder="Profile name (e.g. my-voice, narrator)"
              value={profileName} onChange={e => setProfileName(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Reference transcript — paste the exact text you read in the recording
              </label>
              <textarea rows={3} placeholder="Paste the script text you just read…"
                value={referenceText} onChange={e => setReferenceText(e.target.value)}
                className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
            </div>
            <button onClick={handleCreateProfile}
              disabled={saveStatus === 'saving' || !profileName.trim() || !referenceText.trim() || !cloneModel?.downloaded}
              title={!cloneModel?.downloaded ? 'Waiting for voice clone model to finish downloading' : undefined}
              className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-medium transition-colors">
              {saveStatus === 'saving' ? 'Creating…' : cloneModel?.downloading ? 'Model downloading…' : 'Create Profile'}
            </button>
          </div>
          {saveStatus === 'saved' && <p className="text-sm text-green-600 dark:text-green-400">{saveMsg}</p>}
          {saveStatus === 'error' && <p className="text-sm text-red-500">{saveMsg}</p>}
        </section>
      )}

      {/* Test generation */}
      {(activeProfileId || profiles.length > 0) && (
        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-900 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Test Voice</h2>

          <div className="space-y-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Profile</label>
            <select value={activeProfileId ?? ''} onChange={e => setActiveProfileId(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="">— select a profile —</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.sample_count} sample{p.sample_count !== 1 ? 's' : ''})</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Text to generate</label>
            <textarea rows={3} value={testText} onChange={e => setTestText(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
          </div>

          {!cloneModel?.downloaded && (
            <p className="text-xs text-amber-500">
              {cloneModel?.downloading ? 'Model downloading — generation available once complete.' : 'Model not downloaded. Use the "Start download" button above.'}
            </p>
          )}

          <button onClick={handleGenerate}
            disabled={generating || !activeProfileId || !testText.trim() || !cloneModel?.downloaded}
            className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-medium transition-colors">
            {generating ? 'Generating…' : 'Generate Speech'}
          </button>

          {genError && <p className="text-sm text-red-500">{genError}</p>}

          {generation?.status === 'completed' && audioUrl && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">
                Result {generation.duration ? `— ${generation.duration.toFixed(1)}s` : ''}
              </p>
              <audio controls src={audioUrl} className="w-full rounded-lg" autoPlay />
            </div>
          )}
          {generation?.status === 'failed' && (
            <p className="text-sm text-red-500">Generation failed: {generation.error}</p>
          )}
        </section>
      )}

      {/* Saved profiles */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-900 p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">Saved Profiles</h2>
        {profiles.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">No profiles yet. Record or upload a clip above.</p>
        )}
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {profiles.map(p => (
            <li key={p.id} className="py-3 flex items-center justify-between gap-4">
              <button onClick={() => setActiveProfileId(p.id)}
                className={`flex-1 text-left rounded-lg px-3 py-2 transition-colors ${activeProfileId === p.id ? 'bg-violet-50 dark:bg-violet-950/30 ring-1 ring-violet-400' : 'hover:bg-gray-50 dark:hover:bg-surface-800'}`}>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{p.sample_count} sample{p.sample_count !== 1 ? 's' : ''} · {p.generation_count} generation{p.generation_count !== 1 ? 's' : ''}</p>
              </button>
              <button onClick={() => deleteMutation.mutate(p.id)}
                className="text-xs text-red-400 hover:text-red-600 transition-colors shrink-0">
                Delete
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
